import { Avatar, Flex, Label, Text, useTheme } from '@aws-amplify/ui-react';
import { StorageImage } from '@aws-amplify/ui-react-storage';
import { PublicUserProfile } from '../../utils/PublicUserProfileUtils';
import StorageUtils from '../../utils/StorageUtils';

interface UserThumbnailProps {
    userProfile: PublicUserProfile | null;
    currentUserId?: string; // Optional: if provided and matches userProfile.userId, use dynamic identityId
}

export default function UserThumbnail({ userProfile, currentUserId }: UserThumbnailProps) {
    const { tokens } = useTheme();

    // If no profile is provided, don't render anything
    if (!userProfile) {
        return null;
    }

    // Use dynamic identityId only if viewing current user's profile
    // For other users, we must use stored userIdentityId (which will be updated on their next login)
    const isCurrentUser = currentUserId && userProfile?.userId === currentUserId;
    
    // Determine if we have a valid path to render
    // For current user: we need userId (always have it if isCurrentUser is true)
    // For other users: we need both userIdentityId and userId
    const hasValidPath = userProfile?.userId && (
        isCurrentUser || !!userProfile.userIdentityId
    );
    
    const imagePath = isCurrentUser && userProfile?.userId
        ? ({ identityId }: { identityId?: string }) => {
            // Dynamic identityId for current user - always gets current session identityId
            if (!identityId) {
                // Fallback to stored userIdentityId if dynamic lookup fails
                if (userProfile.userIdentityId) {
                    return StorageUtils.PROFILE_PICTURES + `/${userProfile.userIdentityId}/${userProfile.userId}.jpg`;
                }
                // Return a non-empty but invalid path that will fail gracefully
                // This prevents the empty string error
                return StorageUtils.PROFILE_PICTURES + '/missing/' + userProfile.userId + '.jpg';
            }
            return StorageUtils.PROFILE_PICTURES + `/${identityId}/${userProfile.userId}.jpg`;
        }
        : (userProfile?.userIdentityId && userProfile?.userId)
            ? StorageUtils.PROFILE_PICTURES + `/${userProfile.userIdentityId}/${userProfile.userId}.jpg`
            : undefined; // Use undefined instead of empty string

    return (
        <Flex
            direction="row"
            alignItems="center"
            gap={tokens.space.medium}
        >
            <Avatar size="large">
                {hasValidPath && imagePath ? (
                    <StorageImage
                        className="creator-image"
                        alt=""
                        bucket={StorageUtils.PHOTO_STORAGE_BUCKET_NAME}
                        path={imagePath}
                        onGetUrlError={(error) => {
                            // Silently handle errors - Avatar will show default fallback
                            console.warn("Failed to load user image:", error);
                        }}
                    />
                ) : null}
                {/* Avatar will show default fallback when StorageImage is not rendered or fails */}
            </Avatar>
            <Label fontSize={tokens.fontSizes.large}>
                {userProfile?.nickname || userProfile?.userId || 'Unknown User'}
            </Label>
        </Flex>
    );
} 